import { GDPRSecurityModal } from '@/components/common/GDPRSecurityModal';
import { Logo } from '@/components/common/Logo';
import { ROUTES } from '@/constants/app';
import { NotificationPopover } from '@/features/notifications';
import { useLogout } from '@/hooks/useAuth';
import { useAssistantDisponible } from '@/features/assistant/hooks/useAssistantDisponible';
import { useUnreadCount } from '@/hooks/useNotifications';
import { useAuthStore } from '@/store/auth.store';
import { useNotificationsStore } from '@/store/notifications.store';
import {
  Bell,
  LogOut,
  Menu,
  Settings,
  Shield,
  Sparkles,
  User,
} from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { LanguageToggle } from '../../features/auth/components/LanguageToggle';

export const Header: React.FC<{ onMenuClick: () => void }> = ({
  onMenuClick,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { disponible: assistantDisponible } = useAssistantDisponible();
  const navigate = useNavigate();
  const logout = useLogout();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGDPRModalOpen, setIsGDPRModalOpen] = useState(false);

  // Notifications
  const { unreadCount } = useUnreadCount();
  const { isPopoverOpen, setPopoverOpen } = useNotificationsStore();

  const handleLogout = async () => {
    try {
      await logout.mutateAsync();
      navigate(ROUTES.AUTH.LOGIN);
    } catch {
      // L'erreur est déjà gérée dans le hook useLogout
      navigate(ROUTES.AUTH.LOGIN);
    }
  };

  return (
    <header className="border-b border-neutral-200 bg-white">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={onMenuClick}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 lg:hidden"
              aria-label="Ouvrir le menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* C3 — sur écran étroit, ce lien se réduisait à 4 px de large : une
                cible sous le plancher WCAG 2.5.8 (24 px), sur toutes les pages de
                l'application. Correction signalée dans le récapitulatif C3. */}
            <Link
              to={ROUTES.DASHBOARD}
              className="flex min-h-6 min-w-6 shrink-0 items-center"
            >
              <Logo width={120} height={120} />
            </Link>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 sm:gap-3 lg:gap-4">
            {/* Language Toggle */}
            <div className="hidden sm:block">
              <LanguageToggle />
            </div>

            {/* L'assistant : à un clic depuis n'importe quel écran, sans
                ajouter une entrée au menu (ce n'est pas un territoire, c'est
                une autre façon de faire ce que les écrans font déjà). Montré
                seulement si le serveur l'a activé : pas de porte vers rien. */}
            {assistantDisponible === true && (
              <Link
                to={ROUTES.ASSISTANT}
                className="bg-brand-50 text-brand-700 hover:bg-brand-100 flex min-h-10 items-center gap-2 rounded-lg px-3 py-2 transition-all hover:shadow-sm"
                title={t('header.assistant', 'Assistant')}
              >
                <Sparkles size={18} className="flex-shrink-0" />
                <span className="hidden text-xs font-medium lg:inline">
                  {t('header.assistant', 'Assistant')}
                </span>
              </Link>
            )}

            {/* GDPR Security Button */}
            <button
              onClick={() => setIsGDPRModalOpen(true)}
              className="bg-success-100 text-success-700 hover:bg-success-200 flex items-center gap-2 rounded-lg px-3 py-2 transition-all hover:shadow-sm"
              title={t('gdpr.button')}
            >
              <Shield size={18} className="flex-shrink-0" />
              <span className="hidden text-xs font-medium lg:inline">
                {t('gdpr.button')}
              </span>
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setPopoverOpen(!isPopoverOpen)}
                className="hover:text-brand-600 relative rounded-lg p-2 text-neutral-600 transition-colors hover:bg-neutral-100"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="bg-brand-500 absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <NotificationPopover />
            </div>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="hover:text-brand-600 flex items-center gap-3 rounded-lg p-2 text-neutral-600 transition-colors hover:bg-neutral-100"
              >
                <div className="bg-brand-100 text-brand-700 flex h-8 w-8 items-center justify-center rounded-full">
                  <User size={16} />
                </div>
                <span className="hidden text-sm font-medium text-neutral-900 xl:inline">
                  {user?.firstName} {user?.lastName}
                </span>
              </button>

              {/* Dropdown */}
              {isDropdownOpen && (
                <div className="shadow-medium absolute right-0 z-50 mt-2 w-56 rounded-lg border border-neutral-200 bg-white">
                  <div className="border-b border-neutral-100 p-3">
                    <p className="text-sm font-medium text-neutral-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-neutral-600">{user?.email}</p>
                  </div>

                  <div className="p-1">
                    <Link
                      to={ROUTES.PROFILE}
                      className="hover:bg-brand-50 hover:text-brand-700 flex items-center gap-3 rounded-md px-3 py-2 text-sm text-neutral-700 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <User size={16} />
                      {t('header.profile')}
                    </Link>

                    <Link
                      to={ROUTES.SETTINGS}
                      className="hover:bg-brand-50 hover:text-brand-700 flex items-center gap-3 rounded-md px-3 py-2 text-sm text-neutral-700 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <Settings size={16} />
                      {t('header.settings')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="hover:bg-error-50 hover:text-error-700 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-neutral-700 transition-colors"
                    >
                      <LogOut size={16} />
                      {t('header.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* GDPR Modal */}
      <GDPRSecurityModal
        isOpen={isGDPRModalOpen}
        onClose={() => setIsGDPRModalOpen(false)}
      />
    </header>
  );
};
